/**
 * Meeting form component for create/edit
 */

import {useState, FormEvent, useEffect} from 'react';
import type {CreateMeetingRequest, UpdateMeetingRequest, Meeting} from '@voxapilot/shared';
import {Button} from '../ui/Button';

interface MeetingFormProps {
  initialData?: Partial<CreateMeetingRequest> | Meeting;
  onSubmit: (data: CreateMeetingRequest | UpdateMeetingRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  isEdit?: boolean;
}

export function MeetingForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isEdit = false,
}: MeetingFormProps) {
  const [title, setTitle] = useState(initialData?.title || '');
  const [scheduledDate, setScheduledDate] = useState(
    initialData?.scheduledDate?.split('T')[0] || ''
  );
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [tags, setTags] = useState(initialData?.tags?.join(', ') || '');
  const [participants, setParticipants] = useState(
    initialData?.participants?.join(', ') || ''
  );
  const [location, setLocation] = useState(initialData?.location || '');

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      setTitle(initialData.title || '');
      setScheduledDate(initialData.scheduledDate?.split('T')[0] || '');
      setNotes(initialData.notes || '');
      setTags(initialData.tags?.join(', ') || '');
      setParticipants(initialData.participants?.join(', ') || '');
      setLocation(initialData.location || '');
    }
  }, [initialData]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      const data: UpdateMeetingRequest = {
        title: title || undefined,
        notes: notes || undefined,
        tags: tags
          ? tags.split(',').map((t) => t.trim()).filter(Boolean)
          : undefined,
        participants: participants
          ? participants.split(',').map((p) => p.trim()).filter(Boolean)
          : undefined,
        location: location || undefined,
      };
      await onSubmit(data as CreateMeetingRequest | UpdateMeetingRequest);
    } else {
      const data: CreateMeetingRequest = {
        title,
        scheduledDate: scheduledDate ? new Date(scheduledDate).toISOString() : undefined,
        notes: notes || undefined,
        tags: tags
          ? tags.split(',').map((t) => t.trim()).filter(Boolean)
          : undefined,
        participants: participants
          ? participants.split(',').map((p) => p.trim()).filter(Boolean)
          : undefined,
        location: location || undefined,
      };
      await onSubmit(data);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="title"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Title *
        </label>
        <input
          id="title"
          type="text"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Meeting title"
        />
      </div>

      <div>
        <label
          htmlFor="scheduledDate"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Scheduled Date
        </label>
        <input
          id="scheduledDate"
          type="date"
          value={scheduledDate}
          onChange={(e) => setScheduledDate(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label
          htmlFor="notes"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Notes
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Meeting notes..."
        />
      </div>

      <div>
        <label
          htmlFor="tags"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Tags (comma-separated)
        </label>
        <input
          id="tags"
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="tag1, tag2, tag3"
        />
      </div>

      <div>
        <label
          htmlFor="participants"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Participants (comma-separated)
        </label>
        <input
          id="participants"
          type="text"
          value={participants}
          onChange={(e) => setParticipants(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="email1@example.com, email2@example.com"
        />
      </div>

      <div>
        <label
          htmlFor="location"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Location
        </label>
        <input
          id="location"
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Meeting location"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

